import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { format, subDays } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * useAnalytics - Custom hook for fetching analytics data
 * Separates data-fetching logic from UI components
 */
const useAnalytics = (initialDateRange = null) => {
  const [dateRange, setDateRange] = useState(
    initialDateRange || {
      from: subDays(new Date(), 29),
      to: new Date()
    }
  );
  
  const [analyticsData, setAnalyticsData] = useState(null);
  const [topPages, setTopPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchAnalytics = useCallback(async () => {
    if (!dateRange.from || !dateRange.to) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const startDate = format(dateRange.from, 'yyyy-MM-dd');
      const endDate = format(dateRange.to, 'yyyy-MM-dd');
      
      const [analyticsRes, topPagesRes] = await Promise.all([
        axios.get(`${API}/analytics?start_date=${startDate}&end_date=${endDate}`),
        axios.get(`${API}/analytics/top-pages?start_date=${startDate}&end_date=${endDate}&limit=10`)
      ]);
      
      // Transform data for compatibility with existing components
      const data = analyticsRes.data;
      setAnalyticsData({
        today: data.summary.today,
        this_week: data.summary.this_week,
        this_month: data.summary.this_month,
        this_year: data.summary.this_year,
        daily_data: data.chart_data,
        chart_range: data.date_range
      });
      
      setTopPages(topPagesRes.data.top_pages || []);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError(err.response?.data?.detail || err.message || 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  // Fetch data when dateRange changes
  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  // Preset date range helpers
  const setLast7Days = useCallback(() => {
    setDateRange({
      from: subDays(new Date(), 6),
      to: new Date()
    });
  }, []);

  const setLast30Days = useCallback(() => {
    setDateRange({
      from: subDays(new Date(), 29),
      to: new Date()
    });
  }, []);

  const setLastYear = useCallback(() => {
    setDateRange({
      from: subDays(new Date(), 364),
      to: new Date()
    });
  }, []);

  return {
    // State
    analyticsData,
    topPages,
    loading,
    error,
    dateRange,
    
    // Actions
    setDateRange,
    refetch: fetchAnalytics,
    
    // Preset helpers
    setLast7Days,
    setLast30Days,
    setLastYear
  };
};

export default useAnalytics;
